import { Link } from "react-router-dom";
import { ChevronsUpDown, Pencil, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { useServicesQuery } from "@/hooks/use-services";
import { useServiceStore } from "@/stores/service-store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import type { Service } from "@/types/service";

interface ServiceLogoProps {
  service: Service | null;
  className?: string;
}

export function ServiceLogo({ service, className }: ServiceLogoProps) {
  if (service?.logo_url) {
    return (
      <img
        src={service.logo_url}
        alt=""
        className={cn("size-5 shrink-0 rounded-[5px] object-cover", className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-[5px] bg-primary text-[11px] font-semibold text-primary-foreground",
        className
      )}
    >
      {service?.title?.[0]?.toUpperCase() || "S"}
    </span>
  );
}

export function ServiceSelector() {
  const { data: services, isLoading } = useServicesQuery();
  const { selectedService, setSelectedService } = useServiceStore();

  if (isLoading) {
    return (
      <div className="flex h-11 items-center gap-2 px-2 md:h-8 md:px-2.5">
        <Skeleton className="size-5 rounded-[5px] bg-sidebar-accent md:size-4 md:rounded-[4px]" />
        <Skeleton className="h-3.5 w-24 rounded bg-sidebar-accent" />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex h-11 w-full max-w-full cursor-pointer items-center gap-2 rounded-lg px-2 text-left transition-colors md:h-8 md:w-max md:pr-1.5 md:pl-2.5",
          "hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        )}
      >
        <ServiceLogo
          service={selectedService}
          className="md:size-4 md:rounded-[4px] md:text-[9px]"
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium md:text-[13px]">
          {selectedService?.title || "Select Service"}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground/70" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-(--radix-dropdown-menu-trigger-width) min-w-60"
      >
        <DropdownMenuLabel>Services</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={selectedService?.id}
          onValueChange={(id) => {
            const service = services?.find((s) => s.id === id);
            if (service && service.id !== selectedService?.id) {
              setSelectedService(service, true);
            }
          }}
          className="max-h-[300px] overflow-y-auto"
        >
          {services?.map((service) => (
            <DropdownMenuRadioItem key={service.id} value={service.id}>
              <ServiceLogo service={service} />
              <span className="min-w-0 flex-1 truncate">
                {service.title || "Untitled Service"}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        {selectedService && (
          <DropdownMenuItem asChild>
            <Link to={`/services/${selectedService.id}/edit`}>
              <Pencil />
              Edit service
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/services/new">
            <Plus />
            Create new service
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
